import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header, Divider } from "semantic-ui-react";
import {
  Card,
  Row,
  Col,
  Tag,
  Space,
  Table,
  List,
  Avatar,
  Statistic,
  message,
  Skeleton,
  Button,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../../hooks";
import { partnerDashboardApi } from "../../../../api/partnerDashboardAPI";
import { useTranslation } from "react-i18next";
import "./Page_PartnerDashboard.scss"; // Estilos compartidos

function InfoPair({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        fontSize: 13,
        lineHeight: "20px",
        marginBottom: 4,
      }}
    >
      <div style={{ color: "#6b7280", minWidth: 120 }}>{label}:</div>
      <div style={{ fontWeight: 600 }}>{value || "—"}</div>
    </div>
  );
}

export function Page_PartnerInstitutionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useTranslation(["partners", "common"]);

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  // 1. Carga de datos
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await partnerDashboardApi.getInstitutionDetail(
          accessToken,
          id,
        );
        if (alive) setPayload(data);
      } catch (e) {
        const msg =
          e?.body?.message || e?.message || "No se pudo cargar el detalle";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (id) load();

    return () => {
      alive = false;
    };
  }, [accessToken, id]);

  // 2. Normalización de datos (CORREGIDO con useMemo para evitar el warning de ESLint)
  const { institution, teachers, studentsNames, totals } = useMemo(() => {
    return {
      institution: payload?.institution || {},
      teachers: payload?.teachers || [],
      studentsNames: payload?.studentsNames || [],
      totals: payload?.totals || {},
    };
  }, [payload]);

  // Datos derivados de institution
  const iName = institution.nombre || institution.name || "";
  const iDir = institution.direccion || institution.address || "";
  const iTel = institution.telefono || institution.phone || "";
  const iEmail = institution.email || "";
  const iAvatar =
    institution.avatar && institution.avatar !== "null"
      ? institution.avatar
      : undefined;
  const plan = institution.planEduca || {};

  // 3. Estado Activo/Expirado
  const statusTag = useMemo(() => {
    const exp = institution?.expired_at ? dayjs(institution.expired_at) : null;
    // Si active es true en DB y no ha expirado (o no tiene fecha de exp), es activo
    const isActive =
      institution.active !== false && (!exp || dayjs().isBefore(exp));
    return (
      <Tag color={isActive ? "green" : "red"}>
        {isActive
          ? t("partners:institutionDetail.statusActive")
          : t("partners:institutionDetail.statusExpired")}
      </Tag>
    );
  }, [institution, t]);

  // 4. Columnas de la tabla de profesores
  const teacherColumns = [
    {
      title: t("institutionDetail.colTeacher"),
      dataIndex: "nombre",
      key: "nombre",
      render: (v, row) => (
        <Space orientation="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{v || "—"}</span>
          <span style={{ color: "#6b7280", fontSize: 12 }}>
            {row.email || "—"}
          </span>
        </Space>
      ),
    },
    {
      title: t("institutionDetail.colRoles"),
      key: "roles",
      render: (_, row) =>
        Array.isArray(row.roles) && row.roles.length ? (
          <Space size={[4, 4]} wrap>
            {row.roles.map((r, idx) => (
              <Tag key={idx}>
                {r.role}
                {r.classnumber ? ` · ${r.classnumber}` : ""}
              </Tag>
            ))}
          </Space>
        ) : (
          "—"
        ),
    },
    {
      title: t("institutionDetail.colLearningPaths"),
      dataIndex: ["rutasDidacticas", "count"],
      align: "right",
      width: 150,
      render: (v) => v ?? 0,
    },
  ];

  return (
    <div className="dashboard-partner-page">
      {/* Navegación Superior */}
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ paddingLeft: 0, fontSize: 16 }}
        >
          {t("common:actions.backToList")}
        </Button>
      </div>

      <Header as="h2" style={{ marginBottom: 0 }}>
        {loading ? <Skeleton.Input active size="small" /> : iName}
        <Header.Subheader>
          {t("partners:institutionDetail.detailSubheader")}
        </Header.Subheader>
      </Header>
      <Divider />

      {!!error && <div className="ui negative message">{error}</div>}

      {/* ======== Ficha Principal ======== */}
      <Card bordered hoverable className="detail-card" loading={loading}>
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={10}>
            <Space align="start" size={16}>
              <Avatar
                src={iAvatar}
                alt={iName}
                size={64}
                style={{ background: "#e5e7eb", fontWeight: 700, fontSize: 24 }}
              >
                {(!iAvatar && (iName || "?")[0]) || null}
              </Avatar>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                  {iName || "—"} {statusTag}
                </div>
                <Tag color="blue">
                  {plan?.title || t("partners:institutionDetail.noPlan")}
                </Tag>
                <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
                  {t("partners:institutionDetail.slotsLabel")}:{" "}
                  <b>{plan?.teacherNumber ?? "-"}</b>{" "}
                  {t("partners:institutionDetail.slotsTeachers")} /{" "}
                  <b>{plan?.studentsNumber ?? "-"}</b>{" "}
                  {t("partners:institutionDetail.slotsStudents")}
                </div>
              </div>
            </Space>
          </Col>

          <Col xs={24} md={14}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <InfoPair
                  label={t("partners:institutionDetail.directionLabel")}
                  value={iDir}
                />
                <InfoPair
                  label={t("partners:institutionDetail.phoneLabel")}
                  value={iTel}
                />
                <InfoPair
                  label={t("partners:institutionDetail.emailLabel")}
                  value={iEmail}
                />
              </Col>
              <Col xs={24} sm={12}>
                <InfoPair
                  label={t("partners:institutionDetail.joinDateLabel")}
                  value={
                    institution.created_at
                      ? dayjs(institution.created_at).format("DD/MM/YYYY")
                      : "—"
                  }
                />
                <InfoPair
                  label={t("partners:institutionDetail.expiresLabel")}
                  value={
                    institution.expired_at
                      ? dayjs(institution.expired_at).format("DD/MM/YYYY")
                      : "—"
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* ======== KPIs ======== */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card hoverable className="kpi-card" loading={loading}>
            <Statistic
              title={t("partners:institutionDetail.teachersCard")}
              value={totals?.teachers ?? 0}
              prefix={
                <span role="img" aria-label="t">
                  👨‍🏫
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable className="kpi-card" loading={loading}>
            <Statistic
              title={t("partners:institutionDetail.studentsCard")}
              value={totals?.students ?? 0}
              prefix={
                <span role="img" aria-label="s">
                  🎓
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable className="kpi-card" loading={loading}>
            <Statistic
              title={t("partners:institutionDetail.learningPathsCard")}
              value={totals?.rutasDidacticas ?? 0}
              prefix={
                <span role="img" aria-label="r">
                  📚
                </span>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* ======== Tablas ======== */}
      <Header as="h3">
        {t("partners:institutionDetail.teachersListTitle")}
      </Header>
      <Card hoverable className="table-card" style={{ marginBottom: 24 }}>
        <Table
          size="middle"
          rowKey={(r) => r._id}
          columns={teacherColumns}
          dataSource={teachers}
          pagination={{ pageSize: 5 }}
          loading={loading}
          locale={{ emptyText: t("partners:institutionDetail.noTeachers") }}
        />
      </Card>

      <Header as="h3">
        {t("partners:institutionDetail.studentsListTitle")}
      </Header>
      <Card hoverable className="table-card">
        {loading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          <List
            size="small"
            dataSource={studentsNames}
            bordered
            renderItem={(name) => <List.Item>{name}</List.Item>}
            pagination={{ pageSize: 20, size: "small" }}
            locale={{ emptyText: t("partners:institutionDetail.noStudents") }}
            style={{ maxHeight: 300, overflowY: "auto" }}
          />
        )}
      </Card>
    </div>
  );
}
